import { markRaw, ref, type Component } from 'vue'
import { defineStore } from 'pinia'
import { useModalStore } from '@/apps/core/stores/useModalStore'
import { mergeRefusal, type CreditsRefusal } from '../creditsExhausted'

/**
 * The one « insufficient credits » modal, and what is known about the act it is about
 * (`docs/plans/billing-ui.md` § Wave 1, 1.4).
 *
 * **Opened from two sides, in either order.** The client's handler reports every
 * `402 credits_exhausted` the moment it is answered (`setOnCreditsExhausted`, registered in
 * `entries/app/main.ts`), so no path can end in a toast or a raw error; a caller that knows what
 * the act cost, or that it may be run again, reports the same refusal with that added. Whichever
 * arrives first opens the modal, and the other fills it: the modal reads `refusal` reactively, so a
 * cost or a retry that lands a tick later still reaches the screen.
 *
 * Client state, not a cache: it holds an open dialog and a callback, and is cleared when the dialog
 * closes.
 */
export const useCreditsExhaustedStore = defineStore('billing-credits-exhausted', () => {
  const refusal = ref<CreditsRefusal | null>(null)
  const isOpen = ref(false)

  /** The modal, loaded on the first refusal: most sessions never meet one. */
  let modal: Promise<Component> | null = null

  function loadModal(): Promise<Component> {
    modal ??= import('../modals/CreditsExhaustedModal.vue').then(({ default: component }) =>
      markRaw(component as Component),
    )
    return modal
  }

  async function report(next: CreditsRefusal = {}): Promise<void> {
    refusal.value = mergeRefusal(refusal.value, next)
    if (isOpen.value) return
    isOpen.value = true
    try {
      const component = await loadModal()
      // The modal carries its own header, which changes with the reader's role; the container's
      // title would say the same words twice.
      await useModalStore().open('', component, {
        width: '560px',
        maxWidth: '560px',
        scrollable: true,
        hideHeader: true,
      })
    } finally {
      isOpen.value = false
      refusal.value = null
    }
  }

  return { refusal, isOpen, report }
})
